import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'
import { Button } from '@/components/ui/button'
import {
  CheckCircle2,
  XCircle,
  Database,
  RefreshCw,
  Server,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  status: 'operational' | 'degraded'
  checks: {
    database: boolean
    redis: boolean
  }
  checked_at: string
}

const serviceLabels: Record<string, { label: string; icon: typeof Database }> = {
  database: { label: 'PostgreSQL Database', icon: Database },
  redis: { label: 'Redis Cache & Queue', icon: Server },
}

export default function Status({ status, checks, checked_at }: Props) {
  const [refreshing, setRefreshing] = useState(false)
  const isOperational = status === 'operational'

  function refresh() {
    setRefreshing(true)
    router.reload({
      only: ['status', 'checks', 'checked_at'],
      onFinish: () => setRefreshing(false),
    })
  }

  return (
    <>
      <Head>
        <title>System Status — KonexZero</title>
        <meta name="description" content="Live operational status of KonexZero services. Check API, database, and infrastructure health in real time." />
      </Head>
      <PageLayout>
        <div className="space-y-10">
          <PageHeader
            eyebrow="Status"
            title="System Status"
            description="Current operational status of KonexZero services."
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={refreshing}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', refreshing && 'animate-spin')} />
                Refresh
              </Button>
            }
          />

          {/* Overall status banner — gradient surface, no thick border */}
          <div
            className={cn(
              'surface-card relative overflow-hidden p-6 sm:p-8',
              isOperational ? 'signal-border' : '',
            )}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background: isOperational
                  ? 'radial-gradient(ellipse at top right, hsl(var(--signal-live) / 0.15) 0%, transparent 70%)'
                  : 'radial-gradient(ellipse at top right, hsl(38 95% 55% / 0.15) 0%, transparent 70%)',
              }}
            />
            <div className="relative flex items-center gap-4">
              {isOperational ? (
                <CheckCircle2 className="h-10 w-10 shrink-0 text-[hsl(var(--signal-live))]" />
              ) : (
                <XCircle className="h-10 w-10 shrink-0 text-amber-500" />
              )}
              <div>
                <h2 className="font-heading text-xl font-semibold tracking-tight sm:text-2xl">
                  {isOperational ? 'All Systems Operational' : 'Service Degradation Detected'}
                </h2>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Last checked · {new Date(checked_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <SectionDivider />

          {/* Individual service checks */}
          <section className="space-y-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Services
            </h2>
            <div className="surface-card divide-y divide-foreground/[0.06]">
              {Object.entries(checks).map(([key, healthy]) => {
                const service = serviceLabels[key] || { label: key, icon: Server }
                const Icon = service.icon
                return (
                  <div key={key} className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
                      <span className="text-sm font-medium">{service.label}</span>
                    </div>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]',
                        healthy
                          ? 'bg-[hsl(var(--signal-live)/0.15)] text-[hsl(var(--signal-live))]'
                          : 'bg-destructive/15 text-destructive',
                      )}
                    >
                      {healthy ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          Operational
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" />
                          Down
                        </>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </section>

          <SectionDivider />

          {/* Infrastructure facts */}
          <section className="space-y-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Infrastructure
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { label: 'Media Network', value: 'Global Edge Network', sub: '300+ cities worldwide' },
                { label: 'API Region',    value: 'Multi-region',        sub: 'Automatic failover' },
                { label: 'Encryption',    value: 'TLS 1.3 + DTLS-SRTP', sub: 'All traffic encrypted' },
              ].map((info) => (
                <article key={info.label} className="surface-card p-5">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    {info.label}
                  </div>
                  <p className="mt-2 font-heading text-sm font-semibold">{info.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{info.sub}</p>
                </article>
              ))}
            </div>
          </section>

          <SectionDivider />

        <section className="space-y-2">
          <h2 className="font-heading text-xl font-semibold">Experiencing Issues?</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you're experiencing problems that aren't reflected here, please reach out to our
            support team at{' '}
            <a href="mailto:support@konexzero.com" className="text-primary hover:underline">
              support@konexzero.com
            </a>{' '}
            or visit our{' '}
            <a href="/contact" className="text-primary hover:underline">
              Contact page
            </a>
            .
          </p>
        </section>
      </div>
    </PageLayout>
    </>
  )
}
